import { useState, useCallback, useRef, useEffect } from 'react';
import { httpClient } from '@/utils/httpClient';
import type { StressTestConfig, StressTestProgress, StressTestResult } from '../types';

interface UseStressTestReturn {
  config: StressTestConfig;
  setConfig: React.Dispatch<React.SetStateAction<StressTestConfig>>;
  progress: StressTestProgress | null;
  result: StressTestResult | null;
  history: StressTestResult[];
  isRunning: boolean;
  startTest: () => void;
  cancelTest: () => void;
  error: string | null;
}

const DEFAULT_CONFIG: StressTestConfig = {
  scenario: 'gateway-health',
  concurrentRequests: 10,
  durationSeconds: 10,
};

const POLL_INTERVAL = 1000;

/**
 * Backend-driven stress test hook.
 *
 * Sends test configuration to the Admin API which executes
 * real concurrent HTTP requests server-to-server against the Gateway.
 * The frontend polls for progress every second.
 */
export const useStressTest = (): UseStressTestReturn => {
  const [config, setConfig] = useState<StressTestConfig>(DEFAULT_CONFIG);
  const [progress, setProgress] = useState<StressTestProgress | null>(null);
  const [result, setResult] = useState<StressTestResult | null>(null);
  const [history, setHistory] = useState<StressTestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const testIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  // Load history from backend on mount + clean up polling on unmount
  useEffect(() => {
    httpClient.get<{ data: StressTestResult[] }>('/admin/stress-test/history?start=0&length=50')
      .then(res => { if (res.data?.data?.length) setHistory(res.data.data); })
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = undefined;
    }
  }, []);

  const fetchResult = useCallback(async (testId: string) => {
    try {
      const res = await httpClient.get<StressTestResult>(`/admin/stress-test/${testId}/result`);
      setResult(res.data);
      setHistory(prev => [res.data, ...prev].slice(0, 50));
    } catch { /* result may not be ready yet */ }
  }, []);

  const pollProgress = useCallback((testId: string) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await httpClient.get<StressTestProgress>(`/admin/stress-test/${testId}/progress`);
        setProgress(res.data);

        if (res.data.status !== 'running') {
          stopPolling();
          setIsRunning(false);
          await fetchResult(testId);
        }
      } catch {
        // If polling fails, stop and mark as done
        stopPolling();
        setIsRunning(false);
      }
    }, POLL_INTERVAL);
  }, [stopPolling, fetchResult]);

  const startTest = useCallback(async () => {
    setError(null);
    setResult(null);
    setIsRunning(true);

    try {
      const res = await httpClient.post<{ testId: string }>('/admin/stress-test', {
        scenario: config.scenario,
        accountName: config.accountName,
        concurrentRequests: config.concurrentRequests,
        durationSeconds: config.durationSeconds,
        testImageBase64: config.testImageBase64,
      });

      const testId = res.data.testId;
      testIdRef.current = testId;

      setProgress({
        testId,
        status: 'running',
        completedRequests: 0,
        errors: 0,
        elapsedSeconds: 0,
        totalDurationSeconds: config.durationSeconds,
        currentRps: 0,
        avgLatencyMs: 0,
      });

      pollProgress(testId);
    } catch (e: any) {
      setIsRunning(false);
      const msg = e.data?.error || e.message || 'Error starting stress test';
      setError(msg);
    }
  }, [config, pollProgress]);

  const cancelTest = useCallback(async () => {
    if (!testIdRef.current) return;
    try {
      await httpClient.delete(`/admin/stress-test/${testIdRef.current}`);
    } catch { /* ignore */ }
    stopPolling();
    setIsRunning(false);
    setProgress(prev => prev ? { ...prev, status: 'cancelled' } : null);
  }, [stopPolling]);

  return { config, setConfig, progress, result, history, isRunning, startTest, cancelTest, error };
};
